import { forwardRef, memo, type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import type { BaseComponentProps } from '../../types';

/**
 * Varianti Hero
 */
export type HeroVariant = 'split' | 'centered' | 'video' | 'gradient' | 'minimal';

export interface HeroProps extends HTMLAttributes<HTMLElement>, BaseComponentProps {
  /**
   * Variante hero
   * @default 'split'
   */
  variant?: HeroVariant;
  /**
   * Titolo principale
   */
  title: string;
  /**
   * Sottotitolo
   */
  subtitle?: string;
  /**
   * Descrizione
   */
  description?: string;
  /**
   * Immagine (URL o ReactNode)
   */
  image?: string | React.ReactNode;
  /**
   * Video background (URL)
   */
  videoUrl?: string;
  /**
   * CTA Buttons
   */
  cta?: React.ReactNode;
  /**
   * Overlay opacity per variant video/gradient
   */
  overlayOpacity?: number;
  /**
   * Altezza hero
   */
  height?: 'sm' | 'md' | 'lg' | 'xl' | 'screen';
}

/**
 * Hero Component
 * 
 * Sezione Hero con 5 varianti diverse.
 * Perfetto per landing pages e homepage.
 * Ottimizzato con React.memo per performance.
 * 
 * @example
 * ```tsx
 * <Hero
 *   variant="split"
 *   title="Benvenuto"
 *   subtitle="La tua app"
 *   description="Descrizione qui"
 *   image="/hero.jpg"
 *   cta={<Button>Get Started</Button>}
 * />
 * ```
 */
const HeroComponent = forwardRef<HTMLElement, HeroProps>(
  (
    {
      className,
      variant = 'split',
      title,
      subtitle,
      description,
      image,
      videoUrl,
      cta,
      overlayOpacity = 0.5,
      height = 'lg',
      ...props
    },
    ref
  ) => {
    const heightClasses = {
      sm: 'min-h-[400px]',
      md: 'min-h-[500px]',
      lg: 'min-h-[600px]',
      xl: 'min-h-[700px]',
      screen: 'min-h-screen',
    };

    // Variant: Split (testo left, immagine right)
    if (variant === 'split') {
      return (
        <section
          ref={ref}
          className={cn(
            'w-full bg-base-100',
            heightClasses[height],
            className
          )}
          {...props}
        >
          <div className="container mx-auto px-4 h-full">
            <div className="grid lg:grid-cols-2 gap-12 items-center h-full py-12">
              {/* Content */}
              <div className="space-y-6">
                {subtitle && (
                  <p className="text-primary font-semibold tracking-wide uppercase">
                    {subtitle}
                  </p>
                )}
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-base-content leading-tight">
                  {title}
                </h1>
                {description && (
                  <p className="text-lg md:text-xl text-base-content/70">
                    {description}
                  </p>
                )}
                {cta && (
                  <div className="flex gap-4 flex-wrap">
                    {cta}
                  </div>
                )}
              </div>

              {/* Image */}
              <div className="relative">
                {typeof image === 'string' ? (
                  <img
                    src={image}
                    alt={title}
                    className="w-full h-full object-cover rounded-2xl shadow-2xl"
                  />
                ) : (
                  image
                )}
              </div>
            </div>
          </div>
        </section>
      );
    }

    // Variant: Centered
    if (variant === 'centered') {
      return (
        <section
          ref={ref}
          className={cn(
            'w-full relative bg-base-200',
            heightClasses[height],
            className
          )}
          {...props}
        >
          {/* Background Image */}
          {typeof image === 'string' && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${image})` }}
            >
              <div
                className="absolute inset-0 bg-base-100"
                style={{ opacity: overlayOpacity }}
              />
            </div>
          )}

          {/* Content */}
          <div className="container mx-auto px-4 h-full relative z-10">
            <div className="flex flex-col items-center justify-center h-full text-center space-y-6 max-w-4xl mx-auto">
              {subtitle && (
                <p className="text-primary font-semibold tracking-wide uppercase">
                  {subtitle}
                </p>
              )}
              <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-base-content leading-tight">
                {title}
              </h1>
              {description && (
                <p className="text-lg md:text-xl text-base-content/70 max-w-2xl">
                  {description}
                </p>
              )}
              {cta && (
                <div className="flex gap-4 flex-wrap justify-center">
                  {cta}
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    // Variant: Video
    if (variant === 'video') {
      return (
        <section
          ref={ref}
          className={cn(
            'w-full relative overflow-hidden',
            heightClasses[height],
            className
          )}
          {...props}
        >
          {/* Video Background */}
          {videoUrl && (
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src={videoUrl} type="video/mp4" />
            </video>
          )}

          {/* Overlay */}
          <div
            className="absolute inset-0 bg-base-100"
            style={{ opacity: overlayOpacity }}
          />

          {/* Content */}
          <div className="container mx-auto px-4 h-full relative z-10">
            <div className="flex flex-col items-center justify-center h-full text-center space-y-6 max-w-4xl mx-auto text-white">
              {subtitle && (
                <p className="text-primary-content font-semibold tracking-wide uppercase">
                  {subtitle}
                </p>
              )}
              <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-tight drop-shadow-lg">
                {title}
              </h1>
              {description && (
                <p className="text-lg md:text-xl max-w-2xl drop-shadow-lg">
                  {description}
                </p>
              )}
              {cta && (
                <div className="flex gap-4 flex-wrap justify-center">
                  {cta}
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    // Variant: Gradient
    if (variant === 'gradient') {
      return (
        <section
          ref={ref}
          className={cn(
            'w-full relative overflow-hidden',
            'bg-gradient-to-br from-primary via-secondary to-accent',
            heightClasses[height],
            className
          )}
          {...props}
        >
          {/* Animated Background */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-primary-focus rounded-full blur-3xl animate-pulse" />
            <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-secondary-focus rounded-full blur-3xl animate-pulse delay-1000" />
          </div>

          {/* Content */}
          <div className="container mx-auto px-4 h-full relative z-10">
            <div className="flex flex-col items-center justify-center h-full text-center space-y-6 max-w-4xl mx-auto text-white">
              {subtitle && (
                <p className="font-semibold tracking-wide uppercase text-white/90">
                  {subtitle}
                </p>
              )}
              <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-tight">
                {title}
              </h1>
              {description && (
                <p className="text-lg md:text-xl max-w-2xl text-white/90">
                  {description}
                </p>
              )}
              {cta && (
                <div className="flex gap-4 flex-wrap justify-center">
                  {cta}
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    // Variant: Minimal
    if (variant === 'minimal') {
      return (
        <section
          ref={ref}
          className={cn(
            'w-full bg-base-100',
            heightClasses[height],
            className
          )}
          {...props}
        >
          <div className="container mx-auto px-4 h-full">
            <div className="flex flex-col justify-center h-full space-y-8 max-w-5xl">
              {subtitle && (
                <p className="text-primary font-medium">
                  {subtitle}
                </p>
              )}
              <h1 className="text-5xl md:text-6xl lg:text-8xl font-bold text-base-content leading-none">
                {title}
              </h1>
              {description && (
                <p className="text-xl md:text-2xl text-base-content/60 max-w-3xl">
                  {description}
                </p>
              )}
              {cta && (
                <div className="flex gap-4 flex-wrap">
                  {cta}
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    return null;
  }
);

HeroComponent.displayName = 'Hero';

export const Hero = memo(HeroComponent);

